import type { Dispatch, SetStateAction } from "react";
import { useReducer, useState } from "react";

import type {
  IDependentDropdownTypesProps,
  IDependentMultiselectTypesProps,
} from "./dependentTypes";

import type { IQueryVariablesType } from "@api/dropdowns";
import type { IOption } from "@/interfaces";

/**
 * Checks if the given object is empty or not.
 * @param object | undefined object - The object to be checked for emptiness.
 * @returns boolean - Returns true if the object is empty, otherwise false.
 */
export function isEmptyObject(object: object | undefined) {
  if (object === undefined) {
    return true;
  }
  for (const _key of Object.keys(object)) {
    const key = _key as keyof typeof object;

    if (object[key] === undefined || object[key] === "") {
      return true;
    }
  }

  return false;
}

// this will work with input, dropdowns
// add more checks for new components if needed
function getKey(value: unknown): string {
  const val = value as string | { key: string };

  return typeof val === "string" ? val : val.key;
}

type IDropdownReducerStateType = Record<
  | "country"
  | "state"
  | "hfName"
  | "medicineName"
  | "dosage"
  | "userRoleType"
  | "specialty",
  IOption | undefined
>;

type IMultiSelectReducerStateType = Record<"hfName", IOption | undefined>;

function reducer<T>(state: T, action: { type: keyof T; value: IOption }): T {
  return { ...state, [action.type]: action.value };
}

function checkMismatch<T, TU extends Record<string, IOption | undefined>>(
  values: T,
  fieldName: keyof T | undefined,
  state: TU,
  stateName: keyof TU,
  dispatch: Dispatch<{
    type: keyof TU;
    value: IOption<string>;
  }>,
) {
  const stateValue = state[stateName];

  if (fieldName && stateValue) {
    const value = values[fieldName] as IOption;

    if (value.key !== stateValue.key && value.value !== stateValue.value) {
      dispatch({ type: stateName, value });
      return true;
    }
    if (
      value.key === "" &&
      value.value === "" &&
      (stateValue.value !== "" || stateValue.key !== "")
    ) {
      dispatch({ type: stateName, value });
      return true;
    }
  }
  return false;
}

// #region dropdown dependencies state memorizing

function handleChanged(
  value: boolean,
  isChanged: boolean,
  setIsChanged: Dispatch<SetStateAction<boolean>>,
) {
  if (!isChanged && value) {
    setIsChanged(true);
  }
}

function getDropdownInitialValues<T>(
  values: T,
  props: IDependentDropdownTypesProps<T>,
): IDropdownReducerStateType {
  const initialValues: IDropdownReducerStateType = {
    country: undefined,
    state: undefined,
    hfName: undefined,
    medicineName: undefined,
    dosage: undefined,
    userRoleType: undefined,
    specialty: undefined,
  };

  switch (props.type) {
    case "city":
      return { ...initialValues, state: values[props.stateField] as IOption };
    case "state":
      return {
        ...initialValues,
        country: values[props.countryField] as IOption,
      };
    // TODO: eslint check
    // eslint-disable-next-line sonarjs/no-duplicated-branches
    case "countryTimezone":
      return {
        ...initialValues,
        country: values[props.countryField] as IOption,
      };
    case "dosage":
      return {
        ...initialValues,
        medicineName: values[props.medicineNameField] as IOption,
      };
    case "messagingUsersList":
      return {
        ...initialValues,
        userRoleType: values[props.userRoleTypeField] as IOption,
      };
    // eslint-disable-next-line sonarjs/no-duplicated-branches
    case "userName":
      return {
        ...initialValues,
        userRoleType: values[props.userRoleTypeField] as IOption,
      };
    case "medicineFormulation":
      return {
        ...initialValues,
        medicineName: values[props.medicineNameField] as IOption,
        dosage: values[props.dosageField] as IOption,
      };
    case "subSpeciality":
      return {
        ...initialValues,
        specialty: values[props.specialtyField] as IOption,
      };
  }
}
export function useDropdownQueryParams<T>(
  values: T,
  props: IDependentDropdownTypesProps<T>,
): {
  params: IQueryVariablesType;
  state: IDropdownReducerStateType;
  isChanged: boolean;
} {
  const [state, dispatch] = useReducer(
    reducer<IDropdownReducerStateType>,
    getDropdownInitialValues(values, props),
  );
  const [isChanged, setIsChanged] = useState(false);

  let params: IQueryVariablesType;

  switch (props.type) {
    case "city":
      handleChanged(
        checkMismatch(values, props.stateField, state, "state", dispatch),
        isChanged,
        setIsChanged,
      );

      params = {
        optionType: "city",
        params: {
          state_id: getKey(values[props.stateField!]),
        },
      };
      break;
    case "state":
      handleChanged(
        checkMismatch(values, props.countryField, state, "country", dispatch),
        isChanged,
        setIsChanged,
      );

      params = {
        optionType: "state",
        params: {
          country_id: getKey(values[props.countryField!]),
        },
      };
      break;
    case "countryTimezone":
      handleChanged(
        checkMismatch(values, props.countryField, state, "country", dispatch),
        isChanged,
        setIsChanged,
      );

      params = {
        optionType: "countryTimezone",
        params: {
          country_id: getKey(values[props.countryField!]),
        },
      };
      break;
    case "messagingUsersList":
      handleChanged(
        checkMismatch(
          values,
          props.userRoleTypeField,
          state,
          "userRoleType",
          dispatch,
        ),
        isChanged,
        setIsChanged,
      );

      params = {
        optionType: "messagingUsersList",
        params: undefined,
        queryParam: {
          role_name: getKey(values[props.userRoleTypeField!]),
        },
      };
      break;
    case "userName":
      handleChanged(
        checkMismatch(
          values,
          props.userRoleTypeField,
          state,
          "userRoleType",
          dispatch,
        ),
        isChanged,
        setIsChanged,
      );

      params = {
        optionType: "userName",
        params: { role: getKey(values[props.userRoleTypeField!]) },
      };
      break;
    case "subSpeciality":
      handleChanged(
        checkMismatch(
          values,
          props.specialtyField,
          state,
          "specialty",
          dispatch,
        ),
        isChanged,
        setIsChanged,
      );

      params = {
        optionType: "subSpeciality",
        params: { parent_speciality_id: getKey(values[props.specialtyField!]) },
      };
      break;
    case "dosage":
      handleChanged(
        checkMismatch(
          values,
          props.medicineNameField,
          state,
          "medicineName",
          dispatch,
        ),
        isChanged,
        setIsChanged,
      );
      params = {
        optionType: "dosage",
        params: {
          medicineName: getKey(values[props.medicineNameField]),
        },
      };
      break;
    case "medicineFormulation":
      // handleChanged(
      //   checkMismatch(
      //     values,
      //     props.medicineNameField,
      //     state,
      //     'medicineName',
      //     dispatch
      //   )
      // )

      // check for a mismatch of one of the dependent fields is enough.
      handleChanged(
        checkMismatch(values, props.dosageField, state, "dosage", dispatch),
        isChanged,
        setIsChanged,
      );
      params = {
        optionType: "medicineFormulation",
        params: {
          medicineName: getKey(values[props.medicineNameField]),
          medicineDosage: getKey(values[props.dosageField]),
        },
      };
      break;
  }
  return { params, state, isChanged };
}

// #endregion

// #region multiselect dependencies state memorizing

function getMultiselectInitialValues<T>(
  _values: T,
  props: IDependentMultiselectTypesProps,
): IMultiSelectReducerStateType {
  const initialValues: IMultiSelectReducerStateType = {
    hfName: undefined,
  };

  switch (props.type) {
    case "allDepartments":
      return initialValues;
  }
}

export function useMultiselectQueryParams<T>(
  values: T,
  props: IDependentMultiselectTypesProps,
): {
  params: IQueryVariablesType;
  state: IMultiSelectReducerStateType;
  isChanged: boolean;
} {
  const [state, _dispatch] = useReducer(
    reducer<IMultiSelectReducerStateType>,
    getMultiselectInitialValues(values, props),
  );
  const [isChanged, _setIsChanged] = useState(false);

  let params: IQueryVariablesType;

  switch (props.type) {
    case "allDepartments":
      params = {
        optionType: "allDepartments",
        params: undefined,
      };
      break;
  }
  return { params, state, isChanged };
}
// #endregion
